import { ONE_DAY, ONE_HOUR, ONE_MINUTE, ONE_MONTH, ONE_SECOND, ONE_YEAR, } from '@lucidlink/uicore';
const SECONDS_IN_MINUTE = ONE_MINUTE / ONE_SECOND;
const SECONDS_IN_HOUR = ONE_HOUR / ONE_SECOND;
const SECONDS_IN_DAY = ONE_DAY / ONE_SECOND;
const SECONDS_IN_MONTH = ONE_MONTH / ONE_SECOND;
const SECONDS_IN_YEAR = ONE_YEAR / ONE_SECOND;
export const DURATION_PREFIX = 'P';
export const TIME_PREFIX = 'T';
export const YEARS_SUFFIX = 'Y';
export const MONTHS_SUFFIX = 'M';
export const DAYS_SUFFIX = 'D';
export const HOURS_SUFFIX = 'H';
export const MINUTES_SUFFIX = 'M';
export const SECONDS_SUFFIX = 'S';
export const ZERO_DURATION = `${DURATION_PREFIX}${TIME_PREFIX}0${SECONDS_SUFFIX}`;
/**
 * Converts a duration in milliseconds to ISO 8601 duration format. Duration is absolute and
 * does not take into account months with different number of days or leap years.
 * @param durationMs - Duration in milliseconds (e.g., from Date.now() difference)
 * @returns ISO 8601 duration string (e.g., "PT1H30M5S"). If duration is negative will
 * return a zero duration ISO 8601 string "PT0S".
 * @example
 * getDurationIso(5000) // returns "PT5S"
 * getDurationIso(3665000) // returns "PT1H1M5S"
 * getDurationIso(5500) // returns "PT5.5S"
 * getDurationIso(500) // returns "PT0.5S"
 */
export function getDurationIso(durationMs) {
    if (durationMs <= 0) {
        return ZERO_DURATION;
    }
    const totalSeconds = durationMs / ONE_SECOND;
    let remaining = Math.floor(totalSeconds);
    const years = Math.floor(remaining / SECONDS_IN_YEAR);
    remaining %= SECONDS_IN_YEAR;
    const months = Math.floor(remaining / SECONDS_IN_MONTH);
    remaining %= SECONDS_IN_MONTH;
    const days = Math.floor(remaining / SECONDS_IN_DAY);
    remaining %= SECONDS_IN_DAY;
    const hours = Math.floor(remaining / SECONDS_IN_HOUR);
    remaining %= SECONDS_IN_HOUR;
    const minutes = Math.floor(remaining / SECONDS_IN_MINUTE);
    const wholeSeconds = remaining % SECONDS_IN_MINUTE;
    // Calculate fractional seconds
    const fractionalPart = totalSeconds - Math.floor(totalSeconds);
    const seconds = wholeSeconds + fractionalPart;
    // Build the ISO 8601 duration string
    let duration = DURATION_PREFIX;
    // Date components
    if (years > 0) {
        duration += `${years}${YEARS_SUFFIX}`;
    }
    if (months > 0) {
        duration += `${months}${MONTHS_SUFFIX}`;
    }
    if (days > 0) {
        duration += `${days}${DAYS_SUFFIX}`;
    }
    // Time components
    if (hours > 0 || minutes > 0 || seconds > 0) {
        duration += TIME_PREFIX;
        if (hours > 0) {
            duration += `${hours}${HOURS_SUFFIX}`;
        }
        if (minutes > 0) {
            duration += `${minutes}${MINUTES_SUFFIX}`;
        }
        if (seconds > 0) {
            // Only include fractional part if it exists
            const secondsString = Number.isInteger(seconds)
                ? seconds.toString()
                : seconds.toFixed(3).replace(/\.?0+$/, '');
            duration += `${secondsString}${SECONDS_SUFFIX}`;
        }
    }
    // If the duration is 0, return the minimum valid duration
    return duration === DURATION_PREFIX ? ZERO_DURATION : duration;
}
